import { useEffect, useCallback } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { AuthError } from './auth-error';
import { Onboarding } from './onboarding/index';
import { LoadingScreen } from '@/components/ui/loading-screen';

import { useKizeoAuth } from '@/hooks/kizeo-auth-hooks';
import { embedHooks, ValidationState } from '@/hooks/embed-hooks';
import { useEmbedding } from '@/components/embed-provider';
import { useCreateFlowFromTemplate } from '@/features/flows/hooks/useCreateFlowFromTemplate';
import { kizeoTemplates } from '@/features/templates/lib/templates-kizeo';

interface RenderProps {
  validationState: ValidationState;
  isRegistered: boolean | null;
  authError: boolean | null;
  isPending: boolean;
  signIn: () => void;
}

const RenderValidatedState = ({
  isRegistered,
  authError,
  isPending,
  signIn,
}: Omit<RenderProps, 'validationState'>) => {
  if (isPending) {
    return <LoadingScreen mode="fullscreen" />;
  }

  if (isRegistered === false) {
    return <Onboarding onStart={signIn} />;
  }

  if (authError) {
    return <AuthError onRetry={signIn} />;
  }

  return <LoadingScreen mode="fullscreen" />;
};

const renderByValidationState = (props: RenderProps) => {
  const { validationState } = props;

  switch (validationState) {
    case ValidationState.INVALID:
      return <Navigate to="/404" replace />;

    case ValidationState.VALID:
      return <RenderValidatedState {...props} />;

    case ValidationState.PENDING:
    default:
      return <LoadingScreen mode="fullscreen" />;
  }
};

const KizeoEmbedPage = () => {
  const { i18n } = useTranslation();
  const validationState = embedHooks.useValidateKizeoEmbed();
  const {
    authError,
    signIn,
    isAuthenticated,
    isRegistered,
    isPending,
    checkRegistration,
  } = useKizeoAuth();
  const { embedState } = useEmbedding();
  const navigate = useNavigate();
  const { mutate: createFlow } = useCreateFlowFromTemplate();

  const redirectToTarget = useCallback(() => {
    const selectedTemplateId = embedState.kizeoEmbed?.selectedTemplateId;

    if (selectedTemplateId) {
      const template = kizeoTemplates.find((t) => t.id === selectedTemplateId);
      if (template) {
        createFlow(template);
        return;
      }
    }

    const targetRoute = embedState.kizeoEmbed?.targetRoute || '/flows';
    navigate(targetRoute, { replace: true });
  }, [
    navigate,
    embedState.kizeoEmbed?.targetRoute,
    embedState.kizeoEmbed?.selectedTemplateId,
    createFlow,
  ]);

  useEffect(() => {
    if (validationState !== ValidationState.VALID) return;
    checkRegistration();
  }, [validationState, checkRegistration]);

  useEffect(() => {
    if (isAuthenticated) {
      redirectToTarget();
    }
  }, [isAuthenticated, redirectToTarget]);

  useEffect(() => {
    const kizeoConfigLanguage = embedState.kizeoEmbed?.language;

    if (kizeoConfigLanguage && kizeoConfigLanguage !== i18n.language) {
      i18n.changeLanguage(kizeoConfigLanguage);
    }
  }, [embedState.kizeoEmbed?.language, i18n]);

  return renderByValidationState({
    validationState,
    isRegistered,
    authError,
    isPending,
    signIn,
  });
};

KizeoEmbedPage.displayName = 'KizeoEmbedPage';
export { KizeoEmbedPage };
